import React from 'react';
import { Link } from 'react-router-dom';
import { ExternalLink, Loader2, MapPin, UserRound } from 'lucide-react';
import { ValidationBadge } from './ValidationBadge';
import { CopyTextButton } from '../world/CopyTextButton';
import { mapPath, monsterPath } from '../../utils/worldFormat';
import { getNpc } from '../../data/guide/npcs';
import { EntityThumb } from '../world/EntityThumb';
import { useNpcLocation } from '../../hooks/useNpcLocation';
import type { LocationPoint, ResolvedReference } from '../../utils/npcLocation';
import type { WorldLookup } from '../../utils/progressionLookup';

/**
 * NPC de una guía. La ubicación sale de utils/npcLocation (misma lógica en ficha, misión, receta,
 * buscador y panel). La identidad del catálogo no confirma la ubicación.
 */
export function NpcCard({
  npcId,
  lookup
}: {
  npcId: string;
  lookup?: WorldLookup;
}) {
  const npc = getNpc(npcId);
  const loc = useNpcLocation(npcId);
  if (!npc || !loc) return null;
  const presence = npc.monsterId !== undefined ? lookup?.npc(npc.monsterId) : undefined;
  return <div className="rounded-2xl border border-white/10 bg-[#0A1628] p-3.5" data-id="element-905" data-mp="263fb6ugpmvx2">
      <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-906" data-mp="215nhbuwi8zq8">
        <p className="flex items-center gap-2.5" data-id="element-907" data-mp="1x421q1sl67cc">
          <EntityThumb entity={npc.monsterId !== undefined ? {
          type: 'monster',
          id: npc.monsterId
        } : undefined} name={npc.name} size="card" fallback={<UserRound className="h-4 w-4 text-[#D4AF37]" aria-hidden="true" data-id="element-909" data-mp="2ov83snrvaf49" />} data-id="element-908" data-mp="1x6y0ttg6722d" />
          {presence && npc.monsterId !== undefined ? <Link to={monsterPath(npc.monsterId)} className="font-semibold text-white underline decoration-[#D4AF37]/50 underline-offset-4 hover:text-[#D4AF37]" data-id="element-910" data-mp="2g365ow59tax5">
              {presence.name}
            </Link> : <span className="font-semibold text-white" data-id="element-911" data-mp="38kscly7yfnca">{npc.name}</span>}
        </p>
        <span className="text-xs text-gray-400" data-id="element-912" data-mp="33s8woulsog4b">{npc.role}</span>
      </div>

      <div className="mt-2.5" data-id="element-913" data-mp="3mp0s2esuo09b">
        {loc.state === 'config' && <>
            <Points npcName={npc.name} points={loc.points} data-id="element-914" data-mp="3rocb5ugovhu2" />
            <Provenance data-id="element-915" data-mp="3rnu3m1okth6u">
              <ValidationBadge status="documentado" label="Ubicación documentada en la configuración" data-id="element-916" data-mp="3dx76gdygrzcv" />
              <span data-id="element-917" data-mp="3hyspyi5i7bw1">{loc.date} · no comprobado en el juego</span>
            </Provenance>
          </>}
        {loc.state === 'admin' && <>
            <Points npcName={npc.name} points={loc.points} data-id="element-918" data-mp="33xq1xzuy7rih" />
            {loc.condition && <p className="mt-1 text-xs text-gray-300" data-id="element-919" data-mp="38yxa40106spo">{loc.condition}</p>}
            <Provenance data-id="element-920" data-mp="2uyuukkg64bdl">
              <ValidationBadge status="admin" data-id="element-921" data-mp="2uzo5lxm5tn1j" />
              <span data-id="element-922" data-mp="2yzyat824o9fc">{loc.date} · solo confirma la ubicación, no los requisitos</span>
            </Provenance>
          </>}
        {loc.state === 'reference' && <Reference npcName={npc.name} r={loc.reference} data-id="element-923" data-mp="t7ht65deslma" />}
        {loc.state === 'loading' && <p className="flex items-center gap-2 text-sm text-gray-400" data-id="element-924" data-mp="x81pj6q5rl7d">
            <Loader2 className="h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" data-id="element-925" data-mp="2hpugqew0xj6y" />
            Comprobando la ubicación en la configuración…
          </p>}
        {loc.state === 'error' && <>
            <p className="text-xs text-amber-100" data-id="element-926" data-mp="2rhqchjan0x80">No se pudo cargar la configuración: la ubicación configurada no se pudo comprobar.</p>
            {loc.reference && <Reference npcName={npc.name} r={loc.reference} data-id="element-927" data-mp="31e3f56o5ulew" />}
          </>}
        {loc.state === 'none' && <p className="flex items-center gap-2 text-sm text-gray-300" data-id="element-928" data-mp="2wegvofhqo8xn">
            <MapPin className="h-4 w-4 text-gray-500" aria-hidden="true" data-id="element-929" data-mp="3aft8wj5ilwfu" />
            Ubicación no documentada
          </p>}
      </div>

      {npc.candidateMonsterIds && <details className="mt-2 text-[11px] text-gray-400" data-id="element-930" data-mp="1g1a2i5hur8sj">
          <summary className="flex min-h-[32px] cursor-pointer list-none items-center text-gray-400 hover:text-gray-200" data-id="element-931" data-mp="1g1ijo9l0d09j">Más información</summary>
          <p className="mt-1" data-id="element-932" data-mp="1dh3zc012bmd1">
            En el catálogo existen{' '}
            {npc.candidateMonsterIds.map((mid, i) => <React.Fragment key={mid} data-id="element-933" data-mp="1hhe459d4c6qb" data-mpi={i}>
                {i > 0 && (i === npc.candidateMonsterIds!.length - 1 ? ' y ' : ', ')}
                <Link to={monsterPath(mid)} className="underline decoration-white/20 underline-offset-4 hover:text-[#D4AF37]" data-id="element-934" data-mp="1mibl5ime6uq8">{lookup?.npc(mid)?.name ?? `NPC ${mid}`} ({mid})</Link>
              </React.Fragment>)}
            . Falta confirmar cuál presta este servicio en Vanguard, por eso no se usan sus apariciones.
          </p>
        </details>}
    </div>;
}
function Points({
  npcName,
  points
}: {
  npcName: string;
  points: LocationPoint[];
}) {
  return <ul className="space-y-1" data-id="element-935" data-mp="18ij0o51glaqp">
      {points.map((p, i) => <li key={`${p.mapName}-${i}`} className="flex flex-wrap items-center gap-x-2" data-id="element-936" data-mp="b2vdepsqhd3s" data-mpi={i}>
          <MapPin className="h-4 w-4 shrink-0 text-[#D4AF37]" aria-hidden="true" data-id="element-937" data-mp="g16of3urltnl" />
          {p.mapId !== undefined ? <Link to={mapPath(p.mapId)} className="text-sm font-semibold text-white underline decoration-white/20 underline-offset-4 hover:text-[#D4AF37]" data-id="element-938" data-mp="8i77d4us0e7e">{p.mapName}</Link> : <span className="text-sm font-semibold text-white" data-id="element-939" data-mp="3uom6d7anfxlt">{p.mapName}</span>}
          <span className="font-mono text-[13px] text-gray-200" data-id="element-940" data-mp="3pqavct8mbh20">{p.coords ?? 'coordenadas no documentadas'}</span>
          {p.coords && <CopyTextButton iconOnly text={`${npcName} — ${p.mapName} — ${p.coords}`} label="Copiar ubicación" data-id="element-941" data-mp="3lpim3zzqxc5q" />}
        </li>)}
    </ul>;
}
function Provenance({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px] text-gray-500" data-id="element-942" data-mp="3bz5rtg24q4me">{children}</p>;
}

/** Referencia utilizable con su advertencia. "Ubicación de referencia: Lorencia — X: 122, Y: 110". */
function Reference({
  npcName,
  r
}: {
  npcName: string;
  r: ResolvedReference;
}) {
  return <div data-id="element-943" data-mp="2xzem02lrnujh">
      <p className="text-[11px] font-semibold text-sky-200" data-id="element-944" data-mp="2xzoaflr5xtn2">Ubicación de referencia</p>
      <ul className="mt-0.5 space-y-0.5" data-id="element-945" data-mp="2nc7wi1hss815">
        {r.points.map((p, i) => <li key={`${p.mapName}-${i}`} className="flex flex-wrap items-center gap-x-2" data-id="element-946" data-mp="2sdx7jh50slp8" data-mpi={i}>
            <span className="text-sm text-gray-100" data-id="element-947" data-mp="2eduvwcnb2oif">
              {p.mapId !== undefined ? <Link to={mapPath(p.mapId)} className="font-semibold underline decoration-white/20 underline-offset-4 hover:text-[#D4AF37]" data-id="element-948" data-mp="2jbe8770g0r7q">{p.mapName}</Link> : <span className="font-semibold" data-id="element-949" data-mp="3b33vpjol25if">{p.mapName}</span>}
              {p.coords && <span className="font-mono text-[13px] text-gray-200" data-id="element-950" data-mp="2xbyq80yjaj06"> — {p.coords}</span>}
            </span>
            {p.coords && <CopyTextButton iconOnly text={`${npcName} — ${p.mapName} — ${p.coords} (referencia, pendiente de validar en Vanguard)`} label="Copiar ubicación" data-id="element-951" data-mp="32aibs097tss0" />}
          </li>)}
      </ul>
      {r.condition && <p className="mt-1 text-xs text-gray-300" data-id="element-952" data-mp="3url3htdmvuwt">{r.condition}</p>}
      <p className="mt-1 text-[11px] text-amber-100/90" data-id="element-953" data-mp="80qw6nf1xj01">Pendiente de validar en Vanguard</p>
      <details className="mt-1 text-[11px] text-gray-500" data-id="element-954" data-mp="7ye8ruqiygfl">
        <summary className="flex min-h-[32px] cursor-pointer list-none items-center hover:text-gray-300" data-id="element-955" data-mp="3ynjjvgnc8d6">Fuente de la referencia</summary>
        <p data-id="element-956" data-mp="hselois1spyv">
          {r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline decoration-white/20 underline-offset-4 hover:text-gray-200" data-id="element-957" data-mp="cr7asaulzanz">
              {r.source} <ExternalLink className="h-3 w-3" aria-hidden="true" data-id="element-958" data-mp="3h672lu3ob3b3" />
            </a> : r.source}
          . Referencia de MU Online Season 6; no es configuración de Vanguard ni una prueba en el juego.
        </p>
      </details>
    </div>;
}